import { Check } from "lucide-react";
import ServiceLayout from "@/components/ServiceLayout";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const PROVIDERS = [
  {
    name: "Shipstore",
    tag: "The execution platform",
    image: IMAGES.containersAerial,
    imageAlt: "Aerial view of a shipping container yard",
    body: [
      "Shipstore is the multi-carrier shipping automation platform built for complex operations. It automates shipping decisions across parcel and freight, connects the systems you already run, and gives logistics teams one place to manage carriers, connections, and custom shipping rules.",
      "For operations teams, that means fewer manual steps, fewer errors, and a live view of every shipment.",
    ],
    points: [
      "Automate decisions, packing, routing, and shipping rules",
      "One platform across parcel and freight carriers",
      "Connections with OMS, WMS, ERP, and carrier systems",
      "Automatic carrier rate comparison",
      "Centralized shipment management and visibility",
      "Reporting and analytics for day-to-day operations",
    ],
  },
  {
    name: "ShipSigma",
    tag: "The intelligence layer",
    image: IMAGES.cargoShip,
    imageAlt: "Aerial view of a loaded cargo ship at sea",
    body: [
      "ShipSigma brings transparency to shipping spend. Built on proprietary analytics and an extraordinary depth of shipping data, their team analyzes raw invoice data, audits parcel invoices weekly, and helps shippers secure better carrier agreements.",
      "Where Shipstore executes, ShipSigma analyzes—turning your own shipping data into contract leverage and measurable results.",
    ],
    points: [
      "Shipping spend analysis across carriers",
      "Carrier contract optimization support",
      "Weekly parcel invoice auditing",
      "Shipping analytics through an online platform",
      "Freight services where relevant",
      "Data-driven guidance for carrier negotiations",
    ],
  },
];

export default function Shipping() {
  return (
    <ServiceLayout
      slug="shipping-freight"
      eyebrow="Solutions · Shipping & Freight"
      title="Smarter shipping starts with a clearer picture."
      intro="Shipping is often the largest operational expense no one sees clearly. Riverstone’s Shipping & Freight solution pairs two complementary specialists—Shipstore for execution and ShipSigma for intelligence—so you can simplify operations and understand where every shipping dollar goes."
      image={IMAGES.containersAerial}
      imageAlt="Aerial view of a shipping container yard"
      ctaLabel="Explore Shipping Opportunities"
      whoFor={[
        "B2B, wholesale, e-commerce, and 3PL operations moving regular volume.",
        "Businesses that suspect overspend but lack invoice-level visibility.",
        "Teams juggling multiple carriers, systems, and manual workflows.",
        "Organizations preparing to renegotiate carrier agreements.",
      ]}
      problems={[
        "Manual, error-prone shipping workflows across disconnected systems.",
        "No clear picture of total transportation spend.",
        "Carrier contracts signed without full data in the room.",
        "Invoice errors that go undetected for months.",
      ]}
      capabilities={[
        { title: "Shipping automation", body: "Shipstore automates decisions and workflows across carriers." },
        { title: "Multi-carrier workflows", body: "Parcel and freight managed in one place." },
        { title: "System connections", body: "Integrations with OMS, WMS, ERP, and carrier platforms." },
        { title: "Shipment management & visibility", body: "A live, centralized view of every shipment." },
        { title: "Shipping spend analysis", body: "ShipSigma analyzes invoices to reveal where money goes." },
        { title: "Carrier contract optimization", body: "Data-backed support for better agreements." },
        { title: "Parcel invoice auditing", body: "Weekly audits that catch errors and overcharges." },
        { title: "Shipping analytics", body: "Ongoing insight, not a one-time report." },
      ]}
      providerRole={[
        {
          title: "The specialists’ roles — delivery",
          body: "Shipstore provides the shipping execution platform; ShipSigma provides spend analysis, contract optimization, and invoice auditing. Each is an independent company, and each engagement is delivered by the specialist whose role fits your need.",
        },
        {
          title: "Riverstone’s role — connection & continuity",
          body: "Riverstone helps you decide which capability to start with, makes the introduction, and stays engaged as the work begins. Platform operation and analysis are performed by the providers.",
        },
      ]}
      conversation={[
        { title: "Your shipping today", body: "Volumes, carriers, systems, and the workflows your team currently runs by hand." },
        { title: "Where clarity is missing", body: "Whether execution, spend visibility, or both would move the needle most." },
        { title: "A concrete next step", body: "What an analysis or platform evaluation would involve, and what it would return." },
      ]}
      faqs={[
        { q: "Do we have to change carriers?", a: "No. Optimization and auditing can work with your current carriers; recommendations depend on what the analysis reveals." },
        { q: "What’s the difference between Shipstore and ShipSigma?", a: "Shipstore is an execution platform—it runs and automates shipping. ShipSigma is an intelligence partner—it analyzes spend, audits invoices, and supports contract negotiations." },
        { q: "Is a savings percentage guaranteed?", a: "No. Savings depend on your current contracts, volumes, and invoices. The specialists evaluate first—before anything is promised." },
        { q: "Can they connect to the systems we use?", a: "Shipstore connects to a wide range of OMS, WMS, ERP, and carrier systems. Compatibility with your exact stack is confirmed during evaluation." },
      ]}
      extra={
        <section className="mt-20">
          <Reveal>
            <p className="eyebrow text-forest">Two specialists, two roles</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">
              Execution and intelligence—working together.
            </h2>
          </Reveal>
          <div className="mt-10 space-y-14">
            {PROVIDERS.map((p, idx) => (
              <Reveal key={p.name} delay={0.05}>
                <div className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14`}>
                  <div className={idx % 2 === 1 ? "lg:order-2" : ""}>
                    <img src={p.image} alt={p.imageAlt} width="1200" height="800" loading="lazy" className="aspect-[3/2] w-full object-cover shadow-soft" />
                  </div>
                  <div data-testid={`shipping-provider-${p.name.toLowerCase()}`}>
                    <p className="eyebrow text-river">{p.tag}</p>
                    <h3 className="mt-3 font-serif text-3xl font-light tracking-tight text-navy">{p.name}</h3>
                    {p.body.map((t, i) => (
                      <p key={i} className="mt-4 leading-relaxed text-charcoal/80">{t}</p>
                    ))}
                    <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2 text-sm text-charcoal/80">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      }
    />
  );
}
